/**
 * USDA quality grade bands over the USDA marbling score.
 *
 * Mirrors the server's `usdaGrades` thresholds, which the capture-day summary's
 * Select % / Choice % / Prime % split also reads: Select from 300, Choice from
 * 400, Prime from 700, each band running up to the next. Below Select is
 * Standard, which the summary only ever reports as the remainder. Listed
 * highest grade first, which is also the bands' ordinal sequence.
 */
export type UsdaGradeBandId = "prime" | "choice" | "select" | "standard";

export type UsdaGradeBand = {
  id: UsdaGradeBandId;
  /** Inclusive lower bound of the marbling score. */
  min: number;
  /** Exclusive upper bound of the marbling score. */
  max: number;
};

export const USDA_GRADE_BANDS: readonly UsdaGradeBand[] = [
  { id: "prime", min: 700, max: Infinity },
  { id: "choice", min: 400, max: 700 },
  { id: "select", min: 300, max: 400 },
  { id: "standard", min: -Infinity, max: 300 },
];

const USDA_GRADE_BAND_IDS = new Set<string>(
  USDA_GRADE_BANDS.map((band) => band.id),
);

export const isUsdaGradeBandId = (value: string): value is UsdaGradeBandId =>
  USDA_GRADE_BAND_IDS.has(value);

/** The band a marbling score falls in; undefined for a blank or unparseable
 *  score, which no band claims. */
export const toUsdaGradeBand = (
  score: unknown,
): UsdaGradeBandId | undefined => {
  if (score === null || score === undefined || score === "") return undefined;
  const value = Number(score);
  if (!Number.isFinite(value)) return undefined;
  return USDA_GRADE_BANDS.find((band) => value >= band.min && value < band.max)
    ?.id;
};
